import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { PageHeader, Empty } from "../components/ui-helpers";
import { Plus, ArrowRight, ClipboardText, Trash } from "@phosphor-icons/react";
import { DataResidencyNote } from "../components/DataResidencyNote";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";

const STATUS_STYLE = {
  draft: "bg-gray-100 text-gray-700 border-gray-300",
  submitted: "bg-blue-100 text-blue-800 border-blue-300",
  cop_received: "bg-emerald-100 text-emerald-800 border-emerald-300",
  archived: "bg-slate-100 text-slate-600 border-slate-300",
};
const STATUS_LABEL = {
  draft: "Draft",
  submitted: "Submitted to RE",
  cop_received: "COP received",
  archived: "Archived",
};

export default function RiskAssessmentLanding() {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  const { isRole } = useAuth();

  const deleteIra = async (e, t) => {
    e.stopPropagation();
    if (!window.confirm(`Delete "${t.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/risk-assessments/${t.id}`);
      setRows((prev) => (prev || []).filter((x) => x.id !== t.id));
      toast.success("IRA deleted");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  useEffect(() => {
    api.get("/risk-assessments").then((r) => setRows(r.data.items || [])).catch(() => setRows([]));
  }, []);

  const createNew = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/risk-assessments", { name: "" });
      toast.success(`New IRA created — ${data.applicability_snapshot.length} applicable requirements snapshotted`);
      nav(`/risk-assessments/${data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create IRA");
    } finally { setBusy(false); }
  };

  return (
    <div>
      <div className="flex items-start justify-between gap-4 mb-4">
        <PageHeader title="Inherent Risk Assessment (IRA)"
          subtitle="Complete a Texas-RE-style Risk Assessment for this registration. On creation we snapshot the Standards & Requirements applicable to your registered functions so the assessment window is locked in. Once the Regional Entity returns the Compliance Oversight Plan, upload their response here." />
        <Button data-testid="ira-new-btn" onClick={createNew} disabled={busy} className="shrink-0">
          <Plus size={16} className="mr-1.5" /> {busy ? "Creating…" : "New IRA"}
        </Button>
      </div>
      <DataResidencyNote className="mb-4" />
      {rows === null ? null : rows.length === 0 ? (
        <Empty title="No Risk Assessments yet"
          hint="Click 'New IRA' to start. All 18 Texas RE sections are ready to fill in, and we'll snapshot the applicable NERC standards for your registration."
          icon={<ClipboardText size={24} />} />
      ) : (
        <div className="border border-border" data-testid="ira-list">
          <table className="text-sm w-full">
            <thead className="bg-secondary">
              <tr>{["IRA", "Status", "Applicable reqs", "Submitted", "COP received", "Created", ""].map((h) => (
                <th key={h} className="px-4 py-2 text-left font-semibold">{h}</th>))}</tr>
            </thead>
            <tbody>
              {rows.map((t) => (
                <tr key={t.id} className="border-t border-border hover:bg-secondary/40 cursor-pointer"
                  data-testid={`ira-row-${t.id}`} onClick={() => nav(`/risk-assessments/${t.id}`)}>
                  <td className="px-4 py-3 font-medium">{t.name}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs border px-1.5 py-0.5 rounded ${STATUS_STYLE[t.status] || STATUS_STYLE.draft}`}>
                      {STATUS_LABEL[t.status] || t.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">—</td>
                  <td className="px-4 py-3 text-muted-foreground">{(t.submitted_at || "").slice(0, 10) || "—"}</td>
                  <td className="px-4 py-3 text-muted-foreground">{(t.cop_received_at || "").slice(0, 10) || "—"}</td>
                  <td className="px-4 py-3 text-muted-foreground">{(t.created_at || "").slice(0, 10)}</td>
                  <td className="px-4 py-3 text-muted-foreground">
                    <div className="flex items-center gap-2 justify-end">
                      {isRole("admin") && (
                        <button type="button" onClick={(e) => deleteIra(e, t)}
                                className="text-red-600 hover:text-red-800 p-1"
                                title="Delete IRA" data-testid={`ira-delete-${t.id}`}>
                          <Trash size={16} />
                        </button>
                      )}
                      <ArrowRight size={16} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
